// Electrical category data definitions
import { EMERGENCY_TIMELINE_OPTIONS } from '../../data/constants';

// ==================== ELECTRICAL: Service Categories ====================
export const ELECTRICAL_SERVICE_CATEGORIES = [
  {
    id: "outlets",
    name: "Outlets",
    emoji: "🔌",
    services: [
      { value: "install_new_outlet", label: "Install New Outlet", price: { low: 150, high: 300 } },
      { value: "replace_outlet", label: "Replace Outlet", price: { low: 80, high: 150 } },
      { value: "gfci_outlet", label: "GFCI Outlet", price: { low: 100, high: 200 } },
      { value: "usb_outlet", label: "USB Outlet", price: { low: 100, high: 175 } }
    ]
  },
  {
    id: "lighting",
    name: "Lighting",
    emoji: "💡",
    services: [
      { value: "light_fixture", label: "Light Fixture", price: { low: 100, high: 250 } },
      { value: "ceiling_fan", label: "Ceiling Fan", price: { low: 150, high: 350 } },
      { value: "chandelier", label: "Chandelier", price: { low: 200, high: 450 } },
      { value: "pendant_light", label: "Pendant Light", price: { low: 125, high: 275 } },
      { value: "vanity_light", label: "Vanity Light", price: { low: 100, high: 200 } },
      { value: "exterior_light", label: "Exterior Light", price: { low: 125, high: 275 } },
      { value: "recessed_lighting", label: "Recessed Lighting", price: { low: 150, high: 300 } }
    ]
  },
  {
    id: "wiring",
    name: "Wiring",
    emoji: "🔧",
    services: [
      { value: "new_wiring", label: "New Wiring", price: { low: 200, high: 500 } },
      { value: "wiring_repair", label: "Wiring Repair", price: { low: 150, high: 400 } },
      { value: "wiring_replacement", label: "Wiring Replacement", price: { low: 300, high: 800 } }
    ]
  },
  {
    id: "electrical_panel",
    name: "Electrical Panel",
    emoji: "⚡",
    services: [
      { value: "replace_breaker", label: "Replace Breaker", price: { low: 150, high: 300 } },
      { value: "add_breaker", label: "Add Breaker", price: { low: 175, high: 350 } },
      { value: "panel_upgrade", label: "Panel Upgrade", price: { low: 1500, high: 3500 } },
      { value: "electrical_troubleshooting", label: "Electrical Troubleshooting", price: { low: 100, high: 250 } }
    ]
  },
  {
    id: "smart_home",
    name: "Smart Home",
    emoji: "🏠",
    services: [
      { value: "smart_thermostat", label: "Smart Thermostat", price: { low: 125, high: 275 } },
      { value: "smart_switch", label: "Smart Switch", price: { low: 100, high: 200 } },
      { value: "smart_outlet", label: "Smart Outlet", price: { low: 100, high: 175 } },
      { value: "smart_doorbell", label: "Smart Doorbell", price: { low: 150, high: 300 } },
      { value: "smart_lock", label: "Smart Lock", price: { low: 150, high: 300 } },
      { value: "smart_camera", label: "Smart Camera", price: { low: 125, high: 275 } }
    ]
  },
  {
    id: "audio_low_voltage",
    name: "Audio & Low Voltage",
    emoji: "🔊",
    services: [
      { value: "tv_wiring", label: "TV Wiring", price: { low: 150, high: 350 } },
      { value: "sound_system", label: "Sound System", price: { low: 200, high: 500 } },
      { value: "ethernet_cat6", label: "Ethernet/Cat6", price: { low: 100, high: 250 } },
      { value: "security_cameras", label: "Security Cameras", price: { low: 200, high: 450 } }
    ]
  },
  {
    id: "additional_electrical",
    name: "Additional Services",
    emoji: "🔋",
    services: [
      { value: "ev_charger", label: "EV Charger Installation", price: { low: 500, high: 1500 } },
      { value: "whole_house_surge", label: "Whole House Surge Protector", price: { low: 250, high: 500 } },
      { value: "smoke_detector_install", label: "Smoke Detector Installation", price: { low: 75, high: 150 } },
      { value: "carbon_monoxide_detector", label: "Carbon Monoxide Detector Installation", price: { low: 75, high: 150 } },
      { value: "bathroom_exhaust_fan", label: "Bathroom Exhaust Fan Installation", price: { low: 200, high: 400 } }
    ]
  }
];

// Build price lookup from categories
export const ELECTRICAL_SERVICE_PRICES = {};
ELECTRICAL_SERVICE_CATEGORIES.forEach(cat => {
  cat.services.forEach(svc => {
    ELECTRICAL_SERVICE_PRICES[svc.value] = svc.price;
  });
});

// ==================== ELECTRICAL QUESTIONS ====================
export const ELECTRICAL_QUESTIONS = [
  {
    id: "electrical_services",
    question: "What electrical services do you need?",
    subtitle: "Browse categories below and select all services that apply",
    type: "collapsible_categories",
    categories: ELECTRICAL_SERVICE_CATEGORIES
  },
  
  // Follow-up questions for specific services
  {
    id: "outlet_quantity",
    question: "How many outlets?",
    type: "radio",
    showIfAnyService: ["install_new_outlet", "replace_outlet", "gfci_outlet", "usb_outlet"],
    options: [
      { value: "1", label: "1 outlet" },
      { value: "2-3", label: "2-3 outlets" },
      { value: "4-6", label: "4-6 outlets" },
      { value: "7+", label: "7+ outlets" }
    ]
  },
  {
    id: "lighting_quantity",
    question: "How many light fixtures?",
    type: "radio",
    showIfAnyService: ["light_fixture", "ceiling_fan", "chandelier", "pendant_light", "vanity_light", "exterior_light"],
    options: [
      { value: "1", label: "1 fixture" },
      { value: "2-3", label: "2-3 fixtures" },
      { value: "4-6", label: "4-6 fixtures" },
      { value: "7+", label: "7+ fixtures" }
    ]
  },
  {
    id: "recessed_count",
    question: "How many recessed lights?",
    type: "radio",
    showIfAnyService: ["recessed_lighting"],
    options: [
      { value: "1-4", label: "1-4 lights" },
      { value: "5-8", label: "5-8 lights" },
      { value: "9-12", label: "9-12 lights" },
      { value: "12+", label: "12+ lights" }
    ]
  },
  {
    id: "smart_device_quantity",
    question: "How many smart devices?",
    type: "radio",
    showIfAnyService: ["smart_thermostat", "smart_switch", "smart_outlet", "smart_doorbell", "smart_lock", "smart_camera"],
    options: [
      { value: "1", label: "1 device" },
      { value: "2-3", label: "2-3 devices" },
      { value: "4-6", label: "4-6 devices" },
      { value: "7+", label: "7+ devices" }
    ]
  },
  {
    id: "ev_charger_type",
    question: "What type of EV charger?",
    type: "radio",
    showIfAnyService: ["ev_charger"],
    options: [
      { value: "level_1", label: "Level 1 (120V - Standard outlet)" },
      { value: "level_2", label: "Level 2 (240V - Faster charging)" }
    ]
  },
  {
    id: "panel_current_amps",
    question: "What is your current electrical panel capacity?",
    type: "radio",
    showIfAnyService: ["panel_upgrade"],
    options: [
      { value: "100_amp", label: "100 Amp" },
      { value: "150_amp", label: "150 Amp" },
      { value: "200_amp", label: "200 Amp" },
      { value: "not_sure", label: "Not sure" }
    ]
  },
  
  // Common questions
  {
    id: "home_age",
    question: "How old is your home?",
    type: "radio",
    options: [
      { value: "under_20", label: "Under 20 years" },
      { value: "20-40", label: "20-40 years" },
      { value: "40_plus", label: "40+ years" },
      { value: "not_sure", label: "Not sure" }
    ]
  },
  {
    id: "timeline",
    question: "When do you need this done?",
    type: "radio",
    options: EMERGENCY_TIMELINE_OPTIONS
  }
];
